import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdinkraMark from "@/components/AdinkraMark";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch (e) {
    return "";
  }
}

export default function Certificate() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api
      .get("/certificate")
      .then(({ data }) => setCert(data))
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (user && !user.completed) navigate("/dashboard");
  }, [user, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6 text-center">
        <div>
          <h1 className="font-heading text-purple text-3xl mb-3">Certificate not yet available</h1>
          <p className="text-lav mb-6">Complete all eight modules to unlock your certificate.</p>
          <button onClick={() => navigate("/dashboard")} className="inline-flex items-center gap-2 bg-purple-deep text-cream font-bold rounded-full px-6 py-3">
            <ArrowLeft className="w-4 h-4" /> Back to my course
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-wash py-10 px-4">
      <div className="max-w-3xl mx-auto mb-6 flex justify-between items-center no-print">
        <button onClick={() => navigate("/complete")} className="inline-flex items-center gap-2 text-purple-mid font-semibold hover:text-purple" data-testid="cert-back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-purple-deep text-cream font-bold rounded-full px-6 py-3 hover:bg-purple transition-colors" data-testid="cert-print-btn">
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      {/* Certificate */}
      <div className="max-w-3xl mx-auto bg-cream border border-line rounded-2xl shadow-[0_20px_60px_rgba(34,20,82,0.14)] overflow-hidden" data-testid="certificate">
        <div className="relative p-2 bg-gradient-to-br from-gold-dark via-gold to-gold-pale">
          <div className="bg-cream p-10 sm:p-14 text-center relative">
            {/* Adinkra wax seal — a keepsake stamp of honesty */}
            <div className="absolute top-6 right-6 hidden sm:block" data-testid="cert-seal" aria-label="Adinkra seal of completion">
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-gold-dark via-gold to-gold-pale flex items-center justify-center shadow-[0_6px_22px_rgba(212,175,55,0.45)] ring-2 ring-gold-dark/50">
                <span className="absolute inset-[6px] rounded-full border border-purple-deep/25" />
                <AdinkraMark symbol="adinkrahene" className="text-purple-deep relative z-10" style={{ width: "42px", height: "42px" }} />
              </div>
              <p className="mt-2 text-[9px] uppercase tracking-[0.24em] text-gold-dark font-bold text-center">Sealed in honesty</p>
            </div>
            <img src="/api/htt/assets/swan-platinum.svg" alt="" className="w-20 mx-auto mb-6 opacity-90" />
            <p className="text-[11px] uppercase tracking-[0.28em] text-gold-dark font-bold">Oxytocin-Learning</p>
            <p className="mt-8 text-[13px] uppercase tracking-[0.2em] text-lav">This certifies that</p>
            <h1 className="font-heading text-purple text-[clamp(2rem,6vw,3.4rem)] my-4 leading-tight" data-testid="cert-name">
              {cert?.name || (user?.name || "Practitioner")}
            </h1>
            <div className="gold-rule w-24 mx-auto my-6" />
            <p className="text-ink/75 text-[15px] max-w-lg mx-auto leading-relaxed">
              has completed all eight modules of
            </p>
            <h2 className="font-heading italic text-purple text-3xl sm:text-4xl my-3">How Truth Travels</h2>
            <p className="text-ink/70 text-[15px] max-w-lg mx-auto leading-relaxed">
              a course in receiving, interpreting and answering difficult truth — approximately three hours of CPD-aligned learning.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-8 sm:gap-16 text-center">
              <div>
                <p className="font-heading text-purple text-lg">{formatDate(cert?.completed_at)}</p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-lav mt-1">Date completed</p>
              </div>
              <div>
                <p className="font-heading text-purple text-lg">Edward · Oxytocin-Learning Ltd</p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-lav mt-1">Course director</p>
              </div>
            </div>

            <p className="mt-10 text-[11px] text-lav leading-relaxed max-w-xl mx-auto">
              A certificate of honesty, not merely of attendance. Oxytocin-Learning Ltd is a CPD-accredited training provider, accredited by The CPD Standards Office. Provider No. 51109.
            </p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-gold-dark">Forward Ever, Backward Never</p>

            {/* Mobile seal */}
            <div className="sm:hidden mt-8 flex flex-col items-center" data-testid="cert-seal-mobile">
              <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-gold-dark via-gold to-gold-pale flex items-center justify-center shadow-[0_6px_22px_rgba(212,175,55,0.45)] ring-2 ring-gold-dark/50">
                <span className="absolute inset-[5px] rounded-full border border-purple-deep/25" />
                <AdinkraMark symbol="adinkrahene" className="text-purple-deep relative z-10" style={{ width: "36px", height: "36px" }} />
              </div>
              <p className="mt-2 text-[9px] uppercase tracking-[0.24em] text-gold-dark font-bold">Sealed in honesty</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
